const USER_SETTINGS_CACHE_PREFIX = "hive-user-settings";
const USER_SETTINGS_CACHE_TTL_MS = 5 * 60 * 1000;

const memoryCache = new Map();
const inflightRequests = new Map();

const getCacheKey = (userId) => `${USER_SETTINGS_CACHE_PREFIX}:${userId}`;

const canUseSessionStorage = () => typeof window !== "undefined" && !!window.sessionStorage;

const normalizeSettings = (settings) => (settings && typeof settings === "object" ? settings : {});
const hasCachedSettingsValue = (settings) => Object.keys(normalizeSettings(settings)).length > 0;

export const readCachedUserSettings = (userId) => {
  if (!userId) {
    return null;
  }

  if (memoryCache.has(userId)) {
    const cachedValue = memoryCache.get(userId);
    return hasCachedSettingsValue(cachedValue) ? cachedValue : null;
  }

  if (!canUseSessionStorage()) {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(getCacheKey(userId));
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue);
    if (!parsed?.expiresAt || parsed.expiresAt < Date.now()) {
      window.sessionStorage.removeItem(getCacheKey(userId));
      return null;
    }

    const normalizedSettings = normalizeSettings(parsed.value);
    if (!hasCachedSettingsValue(normalizedSettings)) {
      window.sessionStorage.removeItem(getCacheKey(userId));
      return null;
    }

    memoryCache.set(userId, normalizedSettings);
    return normalizedSettings;
  } catch {
    return null;
  }
};

export const writeCachedUserSettings = (userId, settings) => {
  if (!userId) {
    return normalizeSettings(settings);
  }

  const normalizedSettings = normalizeSettings(settings);
  if (!hasCachedSettingsValue(normalizedSettings)) {
    memoryCache.delete(userId);

    if (canUseSessionStorage()) {
      try {
        window.sessionStorage.removeItem(getCacheKey(userId));
      } catch {}
    }

    return normalizedSettings;
  }

  memoryCache.set(userId, normalizedSettings);

  if (!canUseSessionStorage()) {
    return normalizedSettings;
  }

  try {
    window.sessionStorage.setItem(
      getCacheKey(userId),
      JSON.stringify({
        value: normalizedSettings,
        expiresAt: Date.now() + USER_SETTINGS_CACHE_TTL_MS,
      })
    );
  } catch {}

  return normalizedSettings;
};

export const getCachedUserSettings = async (userId, fetcher) => {
  if (!userId) {
    return {};
  }

  const cachedSettings = readCachedUserSettings(userId);
  if (cachedSettings) {
    return cachedSettings;
  }

  if (inflightRequests.has(userId)) {
    return inflightRequests.get(userId);
  }

  const request = Promise.resolve(fetcher())
    .then((settings) => writeCachedUserSettings(userId, settings))
    .catch((error) => {
      throw error;
    })
    .finally(() => {
      inflightRequests.delete(userId);
    });

  inflightRequests.set(userId, request);
  return request;
};
